/**
 * CR313: Preview Contract Debug Panel
 *
 * Admin-only collapsible panel showing V2 render contract field status.
 * Green = present, red = missing, with values shown.
 */

import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { validateV2RenderDeal, type V2ContractValidation } from "@/lib/deal-cards/validateV2RenderDeal";

interface PreviewContractDebugPanelProps {
  deal: Record<string, unknown> | null;
  className?: string;
}

export function PreviewContractDebugPanel({ deal, className }: PreviewContractDebugPanelProps) {
  const [open, setOpen] = useState(false);

  const validation = useMemo<V2ContractValidation>(
    () => validateV2RenderDeal(deal),
    [deal]
  );

  const { ok, missing, fields } = validation;

  const identityFields = fields.filter(f => f.group === "identity");
  const valueFields = fields.filter(f => f.group === "value");
  const dateFields = fields.filter(f => f.group === "dates");

  const formatValue = (v: unknown): string => {
    if (v == null) return "null";
    if (typeof v === "number") return String(v);
    if (typeof v === "string") return v.length > 30 ? v.slice(0, 27) + "…" : v;
    return JSON.stringify(v).slice(0, 30);
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <div className="flex items-center gap-2 text-[10px] font-mono bg-slate-900 text-slate-300 px-3 py-1.5 rounded-t">
        <ShieldCheck className={cn("h-3 w-3", ok ? "text-green-400" : "text-red-400")} />
        <span className={ok ? "text-green-400" : "text-red-400"}>
          Contract {ok ? "✓ valid" : `✗ ${missing.length} missing`}
        </span>

        {!ok && missing.map(m => (
          <Badge
            key={m}
            variant="outline"
            className="text-[9px] px-1 py-0 h-4 border-red-600 text-red-400"
          >
            {m}
          </Badge>
        ))}

        <div className="flex-1" />

        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-400">
            <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="text-[10px] font-mono bg-slate-950 text-slate-400 px-3 py-2 rounded-b border-t border-slate-800 space-y-1.5">
          {/* Identity */}
          <div className="space-y-0.5">
            <span className="text-slate-500">── Identity ──</span>
            {identityFields.map(f => (
              <FieldRow key={f.key} field={f} formatValue={formatValue} />
            ))}
          </div>

          {/* Value */}
          <div className="space-y-0.5">
            <span className="text-slate-500">── Value ──</span>
            {valueFields.map(f => (
              <FieldRow key={f.key} field={f} formatValue={formatValue} />
            ))}
          </div>

          {/* Dates */}
          <div className="space-y-0.5">
            <span className="text-slate-500">── Dates ──</span>
            {dateFields.map(f => (
              <FieldRow key={f.key} field={f} formatValue={formatValue} />
            ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FieldRow({ field, formatValue }: { field: { key: string; label: string; value: unknown; present: boolean; required: boolean }; formatValue: (v: unknown) => string }) {
  return (
    <div className="flex items-center gap-2">
      {field.present ? (
        <CheckCircle2 className="h-3 w-3 text-green-400 shrink-0" />
      ) : (
        <XCircle className={cn("h-3 w-3 shrink-0", field.required ? "text-red-400" : "text-slate-600")} />
      )}
      <span className="w-32 text-slate-500">{field.label}</span>
      <span className={field.present ? "text-cyan-400" : "text-slate-600"}>
        {formatValue(field.value)}
      </span>
      {field.required && !field.present && (
        <span className="text-red-400 text-[9px]">REQUIRED</span>
      )}
    </div>
  );
}
